import { useState, useRef, useMemo, useCallback, useEffect } from 'react';
import type { TBMEducationEntry } from '../../lib/tbm';
import type { AnnotationData, AnnotationStroke } from '../../lib/inspections';
import { PhotoViewer, AnnotationOverlay } from '../inspection/shared';
import { Button, Card } from '@safetyhub/ui';

interface EducationRecordSectionProps {
  entries: TBMEducationEntry[];
  canAdd: boolean;
  canDelete: boolean;
  canReorder?: boolean;
  readonly?: boolean;
  maxPhotos?: number;
  onAddText: (content: string) => Promise<void>;
  onAddPhoto: (files: File[]) => Promise<void>;
  onDelete: (entryId: string) => Promise<void>;
  onEditText?: (entryId: string, content: string) => Promise<void>;
  onReorder?: (entries: Array<{ id: string; displayOrder: number }>) => Promise<void>;
  annotationApi?: {
    cacheKey: string;
    get: (entryId: string) => Promise<{ data: { version: number; strokes: any[] } } | null>;
    save: (entryId: string, data: AnnotationData) => Promise<any>;
  };
}

export function EducationRecordSection({
  entries,
  canAdd,
  canDelete,
  canReorder = false,
  readonly: isReadonly = false,
  maxPhotos = 10,
  onAddText,
  onAddPhoto,
  onDelete,
  onEditText,
  onReorder,
  annotationApi,
}: EducationRecordSectionProps) {
  const [isAddingText, setIsAddingText] = useState(false);
  const [textContent, setTextContent] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [deletingId, setDeletingId] = useState<string | null>(null);
  const [isReordering, setIsReordering] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editContent, setEditContent] = useState('');
  const [viewerOpen, setViewerOpen] = useState(false);
  const [viewerIndex, setViewerIndex] = useState(0);
  const [annotationRefreshKey, setAnnotationRefreshKey] = useState(0);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const photos = useMemo(
    () => entries
      .filter((e): e is TBMEducationEntry & { url: string } => e.type === 'PHOTO' && !!e.url)
      .map(e => ({ id: e.id, url: e.url, caption: e.caption })),
    [entries],
  );
  const photoCount = photos.length;

  const handleAddText = async () => {
    if (!textContent.trim()) return;
    setIsSubmitting(true);
    try {
      await onAddText(textContent.trim());
      setTextContent('');
      setIsAddingText(false);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(e.target.files || []);
    if (files.length === 0) return;
    const remaining = maxPhotos - photoCount;
    if (remaining <= 0) return;
    const filesToUpload = files.slice(0, remaining);
    setIsSubmitting(true);
    try {
      await onAddPhoto(filesToUpload);
    } finally {
      setIsSubmitting(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  const handleDelete = async (entryId: string) => {
    setDeletingId(entryId);
    try {
      await onDelete(entryId);
    } finally {
      setDeletingId(null);
    }
  };

  const handleEditSave = async () => {
    if (!editingId || !editContent.trim() || !onEditText) return;
    setIsSubmitting(true);
    try {
      await onEditText(editingId, editContent.trim());
      setEditingId(null);
      setEditContent('');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleMoveUp = useCallback(async (index: number) => {
    if (index <= 0 || !onReorder || isReordering) return;
    setIsReordering(true);
    try {
      const newEntries = [...entries];
      [newEntries[index - 1], newEntries[index]] = [newEntries[index], newEntries[index - 1]];
      await onReorder(newEntries.map((e, i) => ({ id: e.id, displayOrder: i })));
    } finally {
      setIsReordering(false);
    }
  }, [entries, onReorder, isReordering]);

  const handleMoveDown = useCallback(async (index: number) => {
    if (index >= entries.length - 1 || !onReorder || isReordering) return;
    setIsReordering(true);
    try {
      const newEntries = [...entries];
      [newEntries[index], newEntries[index + 1]] = [newEntries[index + 1], newEntries[index]];
      await onReorder(newEntries.map((e, i) => ({ id: e.id, displayOrder: i })));
    } finally {
      setIsReordering(false);
    }
  }, [entries, onReorder, isReordering]);

  const renderEntry = (entry: TBMEducationEntry, index: number) => {
    const isDeleting = deletingId === entry.id;
    const deleteButton = canDelete && (
      <button
        onClick={() => handleDelete(entry.id)}
        disabled={isDeleting}
        className="absolute top-2 right-2 bg-black/50 text-white rounded-full w-6 h-6 flex items-center justify-center hover:bg-red-500 transition-colors"
        title="삭제"
      >
        {isDeleting ? (
          <svg className="w-3 h-3 animate-spin" viewBox="0 0 24 24" fill="none">
            <circle cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" className="opacity-25" />
            <path d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" fill="currentColor" className="opacity-75" />
          </svg>
        ) : (
          <svg className="w-3 h-3" viewBox="0 0 20 20" fill="currentColor">
            <path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd" />
          </svg>
        )}
      </button>
    );

    const reorderButtons = canReorder && entries.length > 1 && (
      <div className="flex flex-col gap-0.5 mr-2 flex-shrink-0">
        <button
          onClick={() => handleMoveUp(index)}
          disabled={index === 0 || isReordering}
          className="w-6 h-6 flex items-center justify-center rounded text-ink-400 hover:text-ink-700 hover:bg-surface-hover disabled:opacity-30 disabled:cursor-not-allowed"
          title="위로 이동"
        >
          <svg className="w-3.5 h-3.5" viewBox="0 0 20 20" fill="currentColor">
            <path fillRule="evenodd" d="M14.707 12.707a1 1 0 01-1.414 0L10 9.414l-3.293 3.293a1 1 0 01-1.414-1.414l4-4a1 1 0 011.414 0l4 4a1 1 0 010 1.414z" clipRule="evenodd" />
          </svg>
        </button>
        <button
          onClick={() => handleMoveDown(index)}
          disabled={index === entries.length - 1 || isReordering}
          className="w-6 h-6 flex items-center justify-center rounded text-ink-400 hover:text-ink-700 hover:bg-surface-hover disabled:opacity-30 disabled:cursor-not-allowed"
          title="아래로 이동"
        >
          <svg className="w-3.5 h-3.5" viewBox="0 0 20 20" fill="currentColor">
            <path fillRule="evenodd" d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z" clipRule="evenodd" />
          </svg>
        </button>
      </div>
    );

    if (entry.type === 'TEXT') {
      if (editingId === entry.id) {
        return (
          <div key={entry.id} className="flex items-start">
            {reorderButtons}
            <div className="flex-1 space-y-2">
              <textarea
                value={editContent}
                onChange={(e) => setEditContent(e.target.value)}
                className="w-full border rounded-control p-3 text-sm resize-none focus:ring-2 focus:ring-brand-500 focus:border-brand-500"
                rows={3}
                autoFocus
              />
              <div className="flex gap-2 justify-end">
                <Button
                  size="sm"
                  onClick={() => { setEditingId(null); setEditContent(''); }}
                  disabled={isSubmitting}
                >
                  취소
                </Button>
                <Button
                  variant="primary"
                  size="sm"
                  onClick={handleEditSave}
                  disabled={!editContent.trim() || isSubmitting}
                >
                  {isSubmitting ? '저장 중...' : '저장'}
                </Button>
              </div>
            </div>
          </div>
        );
      }
      return (
        <div key={entry.id} className="flex items-start">
          {reorderButtons}
          <div
            className={`relative flex-1 p-3 bg-surface-soft rounded-card text-sm${onEditText ? ' cursor-pointer hover:bg-surface-hover transition-colors' : ''}`}
            onClick={onEditText ? () => { setEditingId(entry.id); setEditContent(entry.content || ''); } : undefined}
          >
            <p className="whitespace-pre-wrap pr-8">{entry.content}</p>
            {deleteButton}
          </div>
        </div>
      );
    }

    // PHOTO
    return (
      <div key={entry.id} className="flex items-start">
        {reorderButtons}
        <div className="relative flex-1">
          <div
            className="relative cursor-pointer"
            onClick={() => {
              const idx = photos.findIndex(p => p.id === entry.id);
              if (idx >= 0) { setViewerIndex(idx); setViewerOpen(true); }
            }}
          >
            <img
              src={entry.url}
              alt={entry.caption || '교육 현장 사진'}
              className="w-full rounded-card-sm"
            />
            {annotationApi && (
              <EducationAnnotationOverlay entryId={entry.id} annotationApi={annotationApi} refreshKey={annotationRefreshKey} />
            )}
          </div>
          {deleteButton}
          {entry.caption && (
            <p className="mt-1 text-sm text-ink-500">{entry.caption}</p>
          )}
        </div>
      </div>
    );
  };

  return (
    <>
      <Card radius="lg" elevation="none">
        <div className="flex items-center justify-between mb-3">
          <h3 className="font-semibold text-ink-900">
            교육 현장 기록
            {entries.length > 0 && (
              <span className="ml-1 text-sm font-normal text-ink-500">({entries.length}건)</span>
            )}
          </h3>
        </div>

        {entries.length === 0 && !canAdd && (
          <p className="text-sm text-ink-400 text-center py-4">기록이 없습니다.</p>
        )}

        {entries.length > 0 && (
          <div className="space-y-3">
            {entries.map((entry, index) => renderEntry(entry, index))}
          </div>
        )}

        {/* 텍스트 입력 폼 */}
        {isAddingText && (
          <div className="mt-3 space-y-2">
            <textarea
              value={textContent}
              onChange={(e) => setTextContent(e.target.value)}
              placeholder="교육 현장 메모를 입력하세요..."
              className="w-full border rounded-card p-3 text-sm resize-none focus:ring-2 focus:ring-brand-500 focus:border-brand-500"
              rows={3}
              autoFocus
            />
            <div className="flex gap-2 justify-end">
              <Button
                size="sm"
                onClick={() => { setIsAddingText(false); setTextContent(''); }}
                disabled={isSubmitting}
              >
                취소
              </Button>
              <Button
                variant="primary"
                size="sm"
                onClick={handleAddText}
                disabled={!textContent.trim() || isSubmitting}
              >
                {isSubmitting ? '추가 중...' : '추가'}
              </Button>
            </div>
          </div>
        )}

        {/* 액션 버튼 */}
        {canAdd && !isAddingText && (
          <div className="mt-3 flex gap-2">
            <Button
              onClick={() => setIsAddingText(true)}
              disabled={isSubmitting}
            >
              <svg className="w-4 h-4" viewBox="0 0 20 20" fill="currentColor">
                <path d="M13.586 3.586a2 2 0 112.828 2.828l-.793.793-2.828-2.828.793-.793zM11.379 5.793L3 14.172V17h2.828l8.38-8.379-2.83-2.828z" />
              </svg>
              메모 추가
            </Button>
            <Button
              onClick={() => fileInputRef.current?.click()}
              disabled={isSubmitting || photoCount >= maxPhotos}
              title={photoCount >= maxPhotos ? `사진은 최대 ${maxPhotos}장까지 추가할 수 있습니다.` : undefined}
            >
              <svg className="w-4 h-4" viewBox="0 0 20 20" fill="currentColor">
                <path fillRule="evenodd" d="M4 3a2 2 0 00-2 2v10a2 2 0 002 2h12a2 2 0 002-2V5a2 2 0 00-2-2H4zm12 12H4l4-8 3 6 2-4 3 6z" clipRule="evenodd" />
              </svg>
              사진 추가
              {photoCount > 0 && (
                <span className="text-xs text-ink-400">({photoCount}/{maxPhotos})</span>
              )}
            </Button>
            <input
              ref={fileInputRef}
              type="file"
              accept="image/jpeg,image/png,image/heic"
              multiple
              onChange={handleFileChange}
              className="hidden"
            />
          </div>
        )}
      </Card>

      {/* 사진 전체화면 뷰어 */}
      <PhotoViewer
        photos={photos}
        currentIndex={viewerIndex}
        isOpen={viewerOpen}
        onClose={() => { setViewerOpen(false); setAnnotationRefreshKey(k => k + 1); }}
        onIndexChange={setViewerIndex}
        annotation={annotationApi ? {
          companyId: '',
          inspectionId: '',
          readonly: isReadonly,
          customApi: {
            cacheKey: annotationApi.cacheKey,
            get: (entryId) => annotationApi.get(entryId),
            save: (entryId, data) => annotationApi.save(entryId, data),
          },
        } : undefined}
      />
    </>
  );
}

function EducationAnnotationOverlay({
  entryId,
  annotationApi,
  refreshKey,
}: {
  entryId: string;
  annotationApi: NonNullable<EducationRecordSectionProps['annotationApi']>;
  refreshKey: number;
}) {
  const [strokes, setStrokes] = useState<AnnotationStroke[]>([]);

  useEffect(() => {
    let cancelled = false;
    annotationApi.get(entryId).then((res) => {
      if (!cancelled) {
        setStrokes(res?.data?.strokes?.length ? res.data.strokes : []);
      }
    }).catch(() => {});
    return () => { cancelled = true; };
  }, [entryId, annotationApi, refreshKey]);

  if (strokes.length === 0) return null;

  return (
    <AnnotationOverlay
      companyId=""
      inspectionId=""
      photoId=""
      containerWidth={0}
      containerHeight={0}
      strokes={strokes}
      printMode
      visible
    />
  );
}
